import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { PlatformApiError } from './client.ts';
import { useSession } from '../hooks/useSession.tsx';
import {
  type CreateOrgRoleRequest,
  type CreateOrgRoleResponse,
  type DeleteOrgRoleResponse,
  type ListOrgRolesResponse,
  type UpdateOrgRoleRequest,
  type UpdateOrgRoleResponse,
  createOrgRole,
  deleteOrgRole,
  listOrgRoles,
  updateOrgRole,
} from './roles.ts';

/**
 * React Query wrappers for `/v1/org/roles`. The key includes `orgId` so
 * a later multi-org session does not leak another org's matrix. API
 * errors (including the pre-backend 404) are not retried — degraded
 * mode must render immediately.
 */

export const ORG_ROLES_KEY = (orgId: string) => ['org', orgId, 'roles'] as const;

function shouldRetryRoleQuery(failureCount: number, error: Error): boolean {
  if (error instanceof PlatformApiError) {
    return false;
  }
  return failureCount < 1;
}

export function useOrgRoles(orgId: string | undefined): UseQueryResult<ListOrgRolesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgId === undefined ? ['org', 'missing', 'roles'] : ORG_ROLES_KEY(orgId),
    queryFn: ({ signal }) => listOrgRoles(fetchImpl, signal),
    enabled: orgId !== undefined,
    retry: shouldRetryRoleQuery,
  });
}

export function useCreateOrgRole(
  orgId: string | undefined,
): UseMutationResult<CreateOrgRoleResponse, Error, CreateOrgRoleRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createOrgRole(body, fetchImpl),
    onSuccess: () => {
      if (orgId !== undefined) {
        void qc.invalidateQueries({ queryKey: ORG_ROLES_KEY(orgId) });
      }
    },
  });
}

export interface UpdateOrgRoleVars {
  readonly roleId: string;
  readonly body: UpdateOrgRoleRequest;
}

export function useUpdateOrgRole(
  orgId: string | undefined,
): UseMutationResult<UpdateOrgRoleResponse, Error, UpdateOrgRoleVars> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, body }) => updateOrgRole(roleId, body, fetchImpl),
    onSuccess: () => {
      if (orgId !== undefined) {
        void qc.invalidateQueries({ queryKey: ORG_ROLES_KEY(orgId) });
      }
    },
  });
}

export function useDeleteOrgRole(
  orgId: string | undefined,
): UseMutationResult<DeleteOrgRoleResponse, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId) => deleteOrgRole(roleId, fetchImpl),
    onSuccess: () => {
      if (orgId !== undefined) {
        void qc.invalidateQueries({ queryKey: ORG_ROLES_KEY(orgId) });
      }
    },
  });
}
